import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Marker } from '@react-google-maps/api'
import Supercluster from 'supercluster'
import { createClusterIcon } from './clusterIcon'
import type { AlertCounts } from './markerStatus'

// Types
export interface SimpleMarker {
  id: number
  name: string
  address: string
  lastSeen?: string
  value?: number
  position: google.maps.LatLngLiteral
  pointCount?: number // For server-side clustering
  meterNumber?: number | null
  alerts?: AlertCounts
}

interface Cluster {
  id: string
  position: google.maps.LatLngLiteral
  markers: SimpleMarker[]
  count: number
}

interface SuperclusterClusteringProps {
  markers: SimpleMarker[]
  map: google.maps.Map | null
  onMarkerClick?: (marker: SimpleMarker) => void
  onClusterClick?: (cluster: Cluster) => void
  selectedMarkers?: number[]
  markerIcon?: (isSelected: boolean, marker: SimpleMarker) => google.maps.Icon | undefined
  clusterDistance?: number // Distance in pixels to cluster markers
  minZoom?: number // Minimum zoom level to show clusters
  maxZoom?: number // Maximum zoom level to show clusters
  maxZoomClustering?: number // Maximum zoom level where clustering occurs
  isAnimating?: boolean // Whether the map is currently animating
  clickable?: boolean
}

type MapMarkerProps = {
  markerKey: string
  position: google.maps.LatLngLiteral
  icon?: google.maps.Icon
  clickable: boolean
  onClick: (key: string) => void
}

// The map library moves or redraws a marker whenever a prop is a new object,
// even an equal one. Positions are rebuilt on every refetch, so compare them by
// value.
const MapMarker = React.memo(
  ({ markerKey, position, icon, clickable, onClick }: MapMarkerProps) => (
    <Marker
      position={position}
      icon={icon}
      clickable={clickable}
      onClick={() => onClick(markerKey)}
    />
  ),
  (a, b) =>
    a.markerKey === b.markerKey &&
    a.position.lat === b.position.lat &&
    a.position.lng === b.position.lng &&
    a.icon === b.icon &&
    a.clickable === b.clickable &&
    a.onClick === b.onClick
)

// Convert markers to GeoJSON format for Supercluster
const markersToGeoJSON = (markers: SimpleMarker[]) => {
  const geoJSON = markers.map(marker => ({
    type: 'Feature' as const,
    properties: {
      id: marker.id,
      name: marker.name,
      address: marker.address,
      lastSeen: marker.lastSeen,
      value: marker.value,
      marker: marker, // Store original marker data
    },
    geometry: {
      type: 'Point' as const,
      coordinates: [marker.position.lng, marker.position.lat],
    },
  }))
  return geoJSON
}

// Convert Supercluster result back to our format
const superclusterToMarkers = (
  points: any[]
): (SimpleMarker | Cluster)[] => {
  return points.map(point => {
    if (point.properties.cluster) {
      // This is a cluster
      const clusterMarkers: SimpleMarker[] = [] // We can't get individual markers from Supercluster
      return {
        id: `cluster-${point.properties.cluster_id}`,
        position: {
          lat: point.geometry.coordinates[1],
          lng: point.geometry.coordinates[0],
        },
        markers: clusterMarkers,
        count: point.properties.point_count,
      } as Cluster
    } else {
      // This is an individual marker
      const marker = point.properties.marker as SimpleMarker
      return marker
    }
  })
}

export const SuperClustering: React.FC<SuperclusterClusteringProps> = ({
  markers,
  map,
  onMarkerClick,
  onClusterClick,
  selectedMarkers = [],
  markerIcon,
  clusterDistance = 40,
  minZoom = 5,
  maxZoomClustering = 15,
  isAnimating = false,
  clickable = true,
}) => {
  const [clusteredItems, setClusteredItems] = useState<(SimpleMarker | Cluster)[]>([])
  const superclusterRef = useRef<Supercluster | null>(null)
  const lastBoundsRef = useRef<google.maps.LatLngBounds | null>(null)
  const lastZoomRef = useRef<number>(0)
  const isUpdatingRef = useRef<boolean>(false)
  const lastClusteringRef = useRef<{ bounds: number[], zoom: number, timestamp: number } | null>(null)
  

// Create Supercluster instance from the full dataset
const createSupercluster = useCallback((sourceMarkers: SimpleMarker[]) => {
  if (!sourceMarkers.length) return
  const geoJSON = markersToGeoJSON(sourceMarkers)
    superclusterRef.current = new Supercluster({
      radius: clusterDistance,
      maxZoom: maxZoomClustering,
      minZoom: minZoom,
      minPoints: 2,
      extent: 512,
      nodeSize: 64,
    })
    superclusterRef.current.load(geoJSON)
  }, [clusterDistance, maxZoomClustering, minZoom])

  // Update clustering when map or markers change
  const updateClustering = useCallback(async () => {
    if (!map || !markers.length) {
      if (!isAnimating && !isUpdatingRef.current) setClusteredItems([])
      return
    }

    // Server-clustered data can be used directly — no need to wait for animation
    const isServerClustered = markers.some(m => m.pointCount !== undefined)

    if (isServerClustered) {
      setClusteredItems(markers)
      return
    }

    // Client-side clustering needs stable map state, skip during animation
    if (isAnimating || isUpdatingRef.current) return

    isUpdatingRef.current = true

    const bounds = map.getBounds()
    const zoom = map.getZoom() || 0

    if (lastBoundsRef.current && lastZoomRef.current !== undefined) {
      const boundsChanged = bounds ? !bounds.equals(lastBoundsRef.current) : true
      const zoomChanged = Math.abs(zoom - lastZoomRef.current) > 0.5
      if (!boundsChanged && !zoomChanged) {
        isUpdatingRef.current = false
        return
      }
    }

    lastBoundsRef.current = bounds || null
    lastZoomRef.current = zoom

    if (!bounds) {
      setClusteredItems([])
      isUpdatingRef.current = false
      return
    }

    if (!map.getProjection()) {
      setClusteredItems([])
      isUpdatingRef.current = false
      return
    }

    try {
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const boundsArray: [number, number, number, number] = [
        sw.lng(),
        sw.lat(),
        ne.lng(),
        ne.lat(),
      ]
      const currentZoom = Math.floor(zoom)

      const currentClustering = { bounds: boundsArray, zoom: currentZoom, timestamp: Date.now() }
      if (lastClusteringRef.current &&
          lastClusteringRef.current.bounds.every((val, i) => Math.abs(val - boundsArray[i]) < 0.0001) &&
          lastClusteringRef.current.zoom === currentZoom &&
          currentClustering.timestamp - lastClusteringRef.current.timestamp < 1000) {
        isUpdatingRef.current = false
        return
      }
      lastClusteringRef.current = currentClustering

      if (!superclusterRef.current) {
        createSupercluster(markers)
      }

      if (!superclusterRef.current) {
        setClusteredItems([])
        isUpdatingRef.current = false
        return
      }

      const clusters = superclusterRef.current.getClusters(boundsArray, currentZoom)
      const result = superclusterToMarkers(clusters)
      setClusteredItems(result)
    } catch {
      setClusteredItems([])
    } finally {
      isUpdatingRef.current = false
    }
  }, [map, markers, isAnimating, createSupercluster])


  // Update clustering when map changes
  useEffect(() => {
    if (!map) return
    let debounceTimeout: NodeJS.Timeout | null = null
    const handleMapChange = () => {
      if (debounceTimeout) clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(async () => {
        if (!isUpdatingRef.current) {
          await updateClustering()
        }
        debounceTimeout = null
      }, 300)
    }
    const boundsChangedListener = map.addListener('bounds_changed', handleMapChange)
    const zoomChangedListener = map.addListener('zoom_changed', handleMapChange)
    const dragEndListener = map.addListener('dragend', handleMapChange)

    const tryClustering = async () => {
      if (isUpdatingRef.current) return
      const bounds = map.getBounds()
      const projection = map.getProjection()
      if (projection && bounds) {
        await updateClustering()
      } else {
        setClusteredItems([])
      }
    }
    // Run immediately for server-clustered data, with delay for client-side clustering
    const isServerClustered = markers.some(m => m.pointCount !== undefined)
    const firstPass = setTimeout(tryClustering, isServerClustered ? 0 : 800)
    return () => {
      clearTimeout(firstPass)
      if (debounceTimeout) clearTimeout(debounceTimeout)
      google.maps.event.removeListener(boundsChangedListener)
      google.maps.event.removeListener(zoomChangedListener)
      google.maps.event.removeListener(dragEndListener)
    }
  }, [map, updateClustering])

  // The parent passes new click handlers on every render. Reading them through
  // a ref keeps each marker's props unchanged, so unchanged markers skip
  // their update.
  const latest = useRef({
    items: new Map<string, SimpleMarker | Cluster>(),
    onMarkerClick,
    onClusterClick,
  })
  latest.current.onMarkerClick = onMarkerClick
  latest.current.onClusterClick = onClusterClick

  const handleClick = useCallback((key: string) => {
    const { items, onMarkerClick, onClusterClick } = latest.current
    const item = items.get(key)
    if (!item) return
    if ('count' in item) onClusterClick?.(item)
    else if (item.pointCount && item.pointCount > 1)
      // Treat server-side cluster as a regular cluster for zoom behavior
      onClusterClick?.({
        id: `server-cluster-${item.id}`,
        position: item.position,
        markers: [],
        count: item.pointCount,
      })
    else onMarkerClick?.(item)
  }, [])

  const renderItems = useMemo(() => {
    const items = new Map<string, SimpleMarker | Cluster>()
    const rendered = clusteredItems.map(item => {
      // A server cell has no id of its own, but its position is its identity:
      // the same position at the same zoom is the same cell. Keying by it lets
      // React keep the map's marker when a pan or a refetch keeps the cell.
      const key =
        'count' in item ? item.id : `${item.position.lat}:${item.position.lng}`
      items.set(key, item)
      const icon =
        'count' in item
          ? createClusterIcon(item.count)
          : item.pointCount && item.pointCount > 1
            ? createClusterIcon(item.pointCount, item.alerts)
            : markerIcon?.(selectedMarkers.includes(item.id), item)
      return (
        <MapMarker
          key={key}
          markerKey={key}
          position={item.position}
          icon={icon}
          clickable={clickable}
          onClick={handleClick}
        />
      )
    })
    latest.current.items = items
    return rendered
  }, [clusteredItems, selectedMarkers, markerIcon, clickable, handleClick])

  return <>{renderItems}</>
}


